import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Upload, Trash2, Image, History, Receipt, Edit, Save, X, Gamepad2 , ListFilter, ChevronLeft, ChevronRight, Star} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { FileText, Link as LinkIcon, ExternalLink, FileSpreadsheet, FileType, Trash, Crop } from 'lucide-react';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';
import PhotoEditor from '../components/PhotoEditor';


//const CABINET_TYPES = ['Generic EURO', 'Generic US', 'Generic Other', 'Racing', 'Racing Double', 'Musical', 'Pinball'];
const COIN_MECHANISMS = ['yen', 'euro', 'other'];
const AUDIO_SYSTEMS = ['mono', 'stereo'];
const SCREEN_ORIENTATIONS = ['yoko', 'tate', 'both'];
const PLAYING_POSITIONS = ['standing', 'seated'];
const voltages = ['100v', '110v', '220v'];



const CabinetDetail = () => {
  const { id } = useParams();
  const { t, API, canEdit, isAdmin } = useApp();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [cabinet, setCabinet] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [lists, setLists] = useState({});
  const [history, setHistory] = useState([]);
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null);
  const [editingPhoto, setEditingPhoto] = useState(null); // URL "/api/uploads/x.jpg" en cours d'édition
  const [savingEdit, setSavingEdit] = useState(false);
  const [newLink, setNewLink] = useState(''); 
  const [uploadingDoc, setUploadingDoc] = useState(false); 
  const docInputRef = useRef(null); 

  const totalInvested = operations.reduce((sum, op) => {
    if (op.operation_type === 'purchase' || op.operation_type === 'repair' || op.operation_type === 'transport' || op.operation_type === 'purchase_system') {
      return sum + (op.price || 0) + (op.transport_cost || 0) + (op.estimated_cost || 0); 
    }
    return sum;
  }, 0);

  const saleOperation = operations.find(op => op.operation_type === 'sale');
  const salePrice = saleOperation ? (saleOperation.price || 0) : 0;
  
  const profit = salePrice - totalInvested;
  const isSold = !!saleOperation;

  const investorBreakdown = operations.reduce((acc, op) => {
    if (op.price_breakdown && Array.isArray(op.price_breakdown) && op.price_breakdown.length > 0) {
      op.price_breakdown.forEach(investor => {
        const name = investor.nickname || 'Unknown';
        acc[name] = (acc[name] || 0) + (parseFloat(investor.amount) || 0);
      });
    }
    return acc;
  }, {});

  // Détail par investisseur : quelle opération contribue à quel montant (pour expliquer
  // que le total d'un investisseur = somme de ses parts par opération).
  const investorContributions = operations.reduce((acc, op) => {
    (op.price_breakdown || []).forEach((investor) => {
      const name = investor.nickname || 'Unknown';
      (acc[name] = acc[name] || []).push({
        type: op.operation_type,
        amount: parseFloat(investor.amount) || 0,
      });
    });
    return acc;
  }, {});

  useEffect(() => {
    fetchCabinet();
    fetchVendors();
    fetchLists();
  }, [id]);

  const handleDocUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validation basique (optionnel côté front, le back fera le reste)
    const allowedTypes = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.txt'];
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(ext)) {
      toast.error("Format non supporté (PDF, Word, Excel, Texte uniquement)");
      return;
    }

    setUploadingDoc(true);
    const formDataUpload = new FormData();
    formDataUpload.append('document', file);

    try {
      // Note: Assurez-vous d'avoir créé la route correspondante côté serveur (POST /api/cabinets/{id}/documents)
      await axios.post(`${API}/cabinets/${id}/documents`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('common.success'));
      fetchCabinet(); // Rafraîchir pour voir le nouveau doc
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    } finally {
      setUploadingDoc(false);
      if (docInputRef.current) docInputRef.current.value = '';
    }
  };

  const handleDocDelete = async (filename) => {
    try {
      // Note: Assurez-vous d'avoir créé la route correspondante côté serveur
      await axios.delete(`${API}/cabinets/${id}/documents/${filename}`);
      toast.success(t('common.success'));
      fetchCabinet();
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  // --- Gestion des Liens (Links) ---

  const handleAddLink = () => {
    if (!newLink) return;
    
    // Validation URL simple
    const urlPattern = /^(https?:\/\/)/i;
    if (!urlPattern.test(newLink)) {
      toast.error("L'URL doit commencer par http:// ou https://");
      return;
    }

    const currentLinks = formData.links || []; // ou cabinet.links selon votre structure
    // Mise à jour locale (sera sauvegardée avec handleSave global)
    setFormData({ 
      ...formData, 
      links: [...currentLinks, newLink] 
    });
    setNewLink('');
  };

  const handleRemoveLink = (indexToRemove) => {
    const currentLinks = formData.links || [];
    const updatedLinks = currentLinks.filter((_, index) => index !== indexToRemove);
    setFormData({ ...formData, links: updatedLinks });
  };

  const fetchCabinet = async () => {
    try {
      const [cabinetRes, historyRes, operationsRes] = await Promise.all([
        axios.get(`${API}/cabinets/${id}`),
        axios.get(`${API}/history/cabinet/${id}`),
        axios.get(`${API}/operations?cabinet_id=${id}`),
      ]);
      setCabinet(cabinetRes.data);
      setFormData(cabinetRes.data);
      setHistory(historyRes.data);
      setOperations(operationsRes.data);
    } catch (error) {
      toast.error(t('errors.notFound'));
      navigate('/cabinets');
    } finally {
      setLoading(false);
    }
  };

  const sortByValue = (arr) => [...arr].sort((a, b) => (a.value || '').localeCompare(b.value || '', undefined, { sensitivity: 'base' }));

  const fetchVendors = async () => {
    try {
      const response = await axios.get(`${API}/vendors`);
      setVendors([...response.data].sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' })));
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    }
  };

  const fetchLists = async () => {
    try {
      const listTypes = ['screen', 'board', 'control', 'button', 'topper', 'wiring', 'cabinet_type'];
      const results = await Promise.all(listTypes.map((type) => axios.get(`${API}/lists/${type}`)));
      const listsData = {};
      listTypes.forEach((type, index) => {
        listsData[type] = sortByValue(results[index].data);
      });
      setLists(listsData);
    } catch (error) {
      console.error('Failed to fetch lists:', error);
    }
  };

  // Entrée en édition : on aligne l'état sur les valeurs par défaut AFFICHÉES par les
  // Select (value={formData.x || "défaut"}). Sinon, re-sélectionner la valeur déjà
  // montrée ne déclenche pas onValueChange (Radix) et la valeur n'est jamais sauvegardée.
  const startEditing = () => {
    setFormData({
      ...cabinet,
      wiring: cabinet.wiring || 'JAMMA',
      screen_orientation: cabinet.screen_orientation || 'yoko',
      coin_mechanism: cabinet.coin_mechanism || 'euro',
      audio_system: cabinet.audio_system || 'stereo',
      playing_position: cabinet.playing_position || 'seated',
      number_of_players: cabinet.number_of_players || 1,
    });
    setEditing(true);
  };

  const handleSave = async () => {
    try {
      const updateData = {
        friendly_name: formData.friendly_name,
        vendor_id: formData.vendor_id,
        cabinet_type: formData.cabinet_type,
        production_year: formData.production_year,
        in_stock: formData.in_stock,
        screens: formData.screens || [],
        boards: formData.boards || [],
        coin_mechanism: formData.coin_mechanism || null,
        height: formData.height || null,
        width: formData.width || null,
        depth: formData.depth || null,
        weight: formData.weight || null,
        audio_system: formData.audio_system || null,
        screen_orientation: formData.screen_orientation || null,
        wiring: formData.wiring || null,
        playing_position: formData.playing_position || null,
        number_of_players: formData.number_of_players || null,
        controls: formData.controls || [],
        buttons: formData.buttons || [],
        topper: formData.topper || null,
        voltage: formData.voltage || null, 
        links: formData.links || [],
        systemboard: formData.systemboard || null, 
      };

      await axios.put(`${API}/cabinets/${id}`, updateData);
      toast.success(t('common.success'));
      setEditing(false);
      fetchCabinet();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    }
  };


  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (cabinet.photos.length >= 10) {
      toast.error(t('cabinets.maxPhotos'));
      return;
    }

    setUploadingPhoto(true);
    const formData = new FormData();
    formData.append('photo', file);
    formData.append('set_as_main', cabinet.photos.length === 0);

    try {
      await axios.post(`${API}/cabinets/${id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('common.success'));
      fetchCabinet();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    } finally {
      setUploadingPhoto(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handlePhotoDelete = async () => {
    if (!photoToDelete) return;
    const filename = photoToDelete.split('/').pop();
    try {
      await axios.delete(`${API}/cabinets/${id}/photos/${filename}`);
      toast.success(t('common.success'));
      setPhotoToDelete(null);
      fetchCabinet();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    }
  };

  const setAsMainPhoto = async (photoUrl) => {
    try {
      await axios.put(`${API}/cabinets/${id}`, { main_photo: photoUrl });
      toast.success(t('common.success'));
      fetchCabinet();
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  // Sauvegarde une photo éditée (crop/rotation) en remplaçant le fichier existant
  const handleSavePhotoEdit = async (blob) => {
    if (!editingPhoto || !blob) return;
    const filename = editingPhoto.split('/').pop();
    setSavingEdit(true);
    const fd = new FormData();
    fd.append('photo', new File([blob], filename, { type: 'image/jpeg' }));
    try {
      await axios.put(`${API}/cabinets/${id}/photos/${filename}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success(t('common.success'));
      setEditingPhoto(null);
      fetchCabinet();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    } finally {
      setSavingEdit(false);
    }
  };

  // Réordonner : déplace la photo d'un cran (direction -1 = gauche, +1 = droite)
  const movePhoto = async (index, direction) => {
    const photos = cabinet?.photos || [];
    const target = index + direction;
    if (target < 0 || target >= photos.length) return;
    const newPhotos = [...photos];
    [newPhotos[index], newPhotos[target]] = [newPhotos[target], newPhotos[index]];
    try {
      await axios.put(`${API}/cabinets/${id}/photos/order`, { photos: newPhotos });
      fetchCabinet();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errors.serverError'));
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleString();
  const formatCurrency = (value) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value || 0);

  const volume = cabinet?.height && cabinet?.width && cabinet?.depth
    ? ((cabinet.height * cabinet.width * cabinet.depth) / 1000000).toFixed(3)
    : null;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!cabinet) return null;

  return (

  <div> 
    
    <datalist id="screen-list">
      {lists.screen?.map((item) => (
        <option key={item.id} value={item.value} />
      ))}
    </datalist>

    <div className="container mx-auto p-4 space-y-6 pb-20">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/cabinets')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-[0.04em] flex items-center gap-3">
              {cabinet.friendly_name}
              <Badge className={cabinet.in_stock
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 uppercase text-[10px] font-bold'
                : 'bg-secondary/20 text-secondary border border-secondary/40 uppercase text-[10px] font-bold'}>
                {cabinet.in_stock ? 'In Stock' : 'Sold Out'}
              </Badge>
            </h1>
            <p className="text-muted-foreground font-mono text-xs mt-1">{cabinet.unique_id}</p>
          </div>
        </div>
        
        {canEdit && !editing && (
          <Button onClick={startEditing} className="w-full sm:w-auto font-display uppercase tracking-[0.14em] glow-amber">
            <Edit className="h-4 w-4 mr-2" />
            Edit Cabinet
          </Button>
        )}
        
        {editing && (
          <div className="flex gap-2 w-full sm:w-auto">
            <Button variant="outline" onClick={() => { setEditing(false); setFormData(cabinet); }}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={handleSave}>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Photos & Quick Info */}
        <div className="space-y-6">
          <Card className="bezel">
            <CardContent className="p-4">
              <div className="aspect-video bg-muted rounded-lg mb-4 overflow-hidden relative group">
                {cabinet.main_photo ? (
                  <img src={`${API.replace('/api', '')}${cabinet.main_photo}`} alt={cabinet.friendly_name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    <Gamepad2 className="h-12 w-12 opacity-20" />
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-4 gap-2">
                {cabinet.photos.map((photo, index) => {
                  const isMain = cabinet.main_photo === photo;
                  return (
                    <div
                      key={index}
                      className="aspect-square bg-muted rounded cursor-pointer relative group overflow-hidden"
                      onClick={() => setAsMainPhoto(photo)}
                      title={t('cabinets.mainPhoto')}
                    >
                      <img src={`${API.replace('/api', '')}${photo}`} alt={`View ${index}`} className={`w-full h-full object-cover ${isMain ? 'ring-2 ring-primary' : ''}`} />
                      {isMain && (
                        <span className="absolute top-1 left-1 bg-primary text-primary-foreground rounded-sm p-0.5 shadow" title={t('cabinets.mainPhoto')}>
                          <Star className="h-2.5 w-2.5 fill-current" />
                        </span>
                      )}
                      {canEdit && (
                        <Button
                          variant="secondary"
                          size="icon"
                          className="h-6 w-6 absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          title={t('cabinets.editPhoto')}
                          onClick={(e) => { e.stopPropagation(); setEditingPhoto(photo); }}
                        >
                          <Crop className="h-3 w-3" />
                        </Button>
                      )}
                      {canEdit && cabinet.photos.length > 1 && (
                        <div className="absolute bottom-1 inset-x-1 flex justify-between opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            variant="secondary"
                            size="icon"
                            className="h-6 w-6"
                            disabled={index === 0}
                            title={t('common.edit')}
                            onClick={(e) => { e.stopPropagation(); movePhoto(index, -1); }}
                          >
                            <ChevronLeft className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="secondary"
                            size="icon"
                            className="h-6 w-6"
                            disabled={index === cabinet.photos.length - 1}
                            title={t('common.edit')}
                            onClick={(e) => { e.stopPropagation(); movePhoto(index, 1); }}
                          >
                            <ChevronRight className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}

                {canEdit && cabinet.photos.length < 10 && (
                  <div className="aspect-square bg-muted rounded flex items-center justify-center border-2 border-dashed cursor-pointer hover:bg-muted/80" onClick={() => fileInputRef.current?.click()}>
                    {uploadingPhoto ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                    ) : (
                      <Upload className="h-6 w-6 text-muted-foreground" />
                    )}
                  </div>
                )}
                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handlePhotoUpload} />
              </div>
            </CardContent>
          </Card>

          <Card className="bezel">
            <CardHeader>
              <CardTitle>Identity</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {editing ? (
                // MODE ÉDITION
                <>
                  <div className="space-y-2">
                    <Label>Friendly Name</Label>
                    <Input value={formData.friendly_name} onChange={(e) => setFormData({ ...formData, friendly_name: e.target.value })} />
                  </div>

                  <div className="space-y-2">
                    <Label>Vendor</Label>
                    <Select value={formData.vendor_id} onValueChange={(v) => setFormData({ ...formData, vendor_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select vendor" />
                      </SelectTrigger>
                      <SelectContent>
                        {vendors.map((v) => (
                          // PROTECTION : on s'assure que v.id n'est pas vide
                          <SelectItem key={v.id} value={v.id}>
                            {v.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select value={formData.cabinet_type} onValueChange={(v) => setFormData({ ...formData, cabinet_type: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {(lists.cabinet_type || []).length > 0 ? (
                               lists.cabinet_type.map((w) => (
                                 <SelectItem key={w.id} value={w.value}>{w.value}</SelectItem>
                               ))
                             ) : (
                               <SelectItem value="GENERIC">Generic</SelectItem>
                             )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Production Year</Label>
                    <Input 
                        type="number"
                        placeholder="e.g. 1995"
                        value={formData.production_year || ''} 
                        onChange={(e) => setFormData({ ...formData, production_year: parseInt(e.target.value) || null })} 
                    />
                  </div>

                  <div className="flex items-center space-x-2 py-2">
                    <Checkbox id="in_stock" checked={formData.in_stock} onCheckedChange={(c) => setFormData({ ...formData, in_stock: c })} />
                    <Label htmlFor="in_stock">In Stock</Label>
                  </div>
                </>
              ) : (
                // MODE LECTURE
                <>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Vendor</span>
                    <span className="font-medium">{cabinet.vendor_name}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Type</span>
                    <span className="font-medium">{cabinet.cabinet_type}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Year</span>
                    <span className="font-medium">{cabinet.production_year || '-'}</span>
                  </div>
                  <div className="flex justify-between py-2 border-b">
                    <span className="text-muted-foreground">Created</span>
                    <span className="font-medium">{new Date(cabinet.created_at).toLocaleDateString()}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Middle/Right: Specs & History */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="specs">
            <TabsList className="w-full h-auto flex flex-wrap justify-start gap-2 bg-card border border-border p-2 rounded-lg bezel">
              <TabsTrigger className="flex-1" value="specs">Specifications</TabsTrigger>
              <TabsTrigger className="flex-1" value="operations">Operations</TabsTrigger>
              <TabsTrigger className="flex-1" value="gallery">Photo Gallery</TabsTrigger>
              <TabsTrigger className="flex-1" value="library">Library (sheets)</TabsTrigger>
              <TabsTrigger className="flex-1" value="links">Links</TabsTrigger>
              <TabsTrigger className="flex-1" value="history">History</TabsTrigger>
            </TabsList>
          

            <TabsContent value="specs" className="space-y-4">
              <Card className="bezel">
                <CardContent className="p-6">
                  {editing ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Physical Dimensions */}
                      <div className="space-y-2">
                         <Label>Dimensions ({t('cabinets.dimensionslabel')})</Label>
                         <div className="flex gap-2">
                           <Input placeholder="H" type="number" value={formData.height || ''} onChange={(e) => setFormData({ ...formData, height: parseFloat(e.target.value) })} />
                           <Input placeholder="W" type="number" value={formData.width || ''} onChange={(e) => setFormData({ ...formData, width: parseFloat(e.target.value) })} />
                           <Input placeholder="D" type="number" value={formData.depth || ''} onChange={(e) => setFormData({ ...formData, depth: parseFloat(e.target.value) })} />
                         </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Weight (kg)</Label>
                        <Input type="number" value={formData.weight || ''} onChange={(e) => setFormData({ ...formData, weight: parseFloat(e.target.value) })} />
                      </div>

                      {/* Configurations */}
                      <div className="space-y-2">
                        <Label>Wiring</Label>
                        <Select value={formData.wiring || "JAMMA"} onValueChange={(v) => setFormData({ ...formData, wiring: v })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select wiring" />
                          </SelectTrigger>
                          <SelectContent>
                             {(lists.wiring || []).length > 0 ? (
                               lists.wiring.map((w) => (
                                 <SelectItem key={w.id} value={w.value}>{w.value}</SelectItem>
                               ))
                             ) : (
                               <SelectItem value="JAMMA">JAMMA (Default)</SelectItem>
                             )}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Orientation</Label>
                        <Select value={formData.screen_orientation || "yoko"} onValueChange={(v) => setFormData({ ...formData, screen_orientation: v })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select orientation" />
                          </SelectTrigger>
                          <SelectContent>
                            {SCREEN_ORIENTATIONS.map((orientation) => (
                              <SelectItem key={orientation} value={orientation}>
                                {orientation === 'both' ? t('cabinets.screenOrientationVal3') : orientation}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  ) : (
                    // Display Mode for Specs
                    <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                      <div>
                        <h3 className="font-semibold mb-2">Physical</h3>
                        <dl className="space-y-1 text-sm">
                          <div className="flex justify-between"><dt className="text-muted-foreground">Dimensions:</dt> <dd>{cabinet.height || '-'} x {cabinet.width || '-'} x {cabinet.depth || '-'} cm</dd></div>
                          <div className="flex justify-between"><dt className="text-muted-foreground">{t('cabinets.weight')}:</dt> <dd>{cabinet.weight ? `${cabinet.weight} kg` : '-'}</dd></div>
                          {volume && (
                           <div className="flex justify-between"><dt className="text-muted-foreground">Volume</dt>
                              <dd>
                                {volume} m³
                              </dd>
                          </div>
                          )}
                        </dl>
                      </div>
                      <div>
                        <h3 className="font-semibold mb-2">Configuration</h3>
                        <dl className="space-y-1 text-sm">
                          <div className="flex justify-between"><dt className="text-muted-foreground">{t('cabinets.wiring')}:</dt> <dd>{cabinet.wiring || '-'}</dd></div>
                          <div className="flex justify-between"><dt className="text-muted-foreground">{t('cabinets.orientationlbl')}:</dt> <dd>
                            {cabinet.screen_orientation === 'both' 
                              ? t('cabinets.screenOrientationVal3') 
                              : cabinet.screen_orientation || '-'
                            }
                          </dd></div>
                          <div className="flex justify-between"><dt className="text-muted-foreground">Position:</dt> <dd>{cabinet.playing_position || '-'}</dd></div>
                        </dl>
                      </div>
                    </div>
                  )}

                  
                </CardContent>
              </Card>

              <Card className="bezel">
                <CardHeader>
                  <CardTitle>Screen(s)</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* --- Screen 1 Reference --- */}
                  <div className="space-y-2">
                    <Label>Screen 1 Reference</Label>
                    {editing ? (
                      <Input 
                        list="screen-list"
                        placeholder="Type to search screen..."
                        value={formData.screens?.[0] || ''} 
                        onChange={(e) => {
                          const newScreens = [...(formData.screens || [])];
                          newScreens[0] = e.target.value;
                          setFormData({ ...formData, screens: newScreens });
                        }}
                      />
                    ) : (
                      <div className="font-medium h-10 flex items-center">{cabinet.screens?.[0] || '-'}</div>
                    )}
                  </div>

                  {/* --- Screen 1 Board --- */}
                  <div className="space-y-2">
                    <Label>Screen 1 Board Reference</Label>
                    {editing ? (
                      <Input 
                        placeholder="Chassis/Board ref..."
                        value={formData.boards?.[0] || ''} 
                        onChange={(e) => {
                          const newBoards = [...(formData.boards || [])];
                          newBoards[0] = e.target.value;
                          setFormData({ ...formData, boards: newBoards });
                        }}
                      />
                    ) : (
                      <div className="font-medium h-10 flex items-center">{cabinet.boards?.[0] || '-'}</div>
                    )}
                  </div>


                  {/* --- Screen 2 Reference --- */}
                  <div className="space-y-2">
                    <Label>Screen 2 Reference</Label>
                    {editing ? (
                      <Input 
                        list="screen-list"
                        placeholder="Type to search screen..."
                        value={formData.screens?.[1] || ''} 
                        onChange={(e) => {
                          const newScreens = [...(formData.screens || [])];
                          newScreens[1] = e.target.value;
                          setFormData({ ...formData, screens: newScreens });
                        }}
                      />
                    ) : (
                      <div className="font-medium h-10 flex items-center">{cabinet.screens?.[1] || '-'}</div>
                    )}
                  </div>

                  {/* --- Screen 2 Board --- */}
                  <div className="space-y-2">
                    <Label>Screen 2 Board Reference</Label>
                    {editing ? (
                      <Input 
                        placeholder="Chassis/Board ref..."
                        value={formData.boards?.[1] || ''} 
                        onChange={(e) => {
                          const newBoards = [...(formData.boards || [])];
                          newBoards[1] = e.target.value;
                          setFormData({ ...formData, boards: newBoards });
                        }}
                      />
                    ) : (
                      <div className="font-medium h-10 flex items-center">{cabinet.boards?.[1] || '-'}</div>
                    )}
                  </div>
                  </CardContent>
              </Card>

              <Card className="bezel">
                <CardHeader>
                  <CardTitle>Slot or PCB Board</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    {editing ? (
                    <Select value={formData.systemboard || "-"} onValueChange={(v) => setFormData({ ...formData, systemboard: v })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select System board" />
                          </SelectTrigger>
                          <SelectContent>
                            {(lists.board || []).length > 0 ? (
                                  lists.board.map((w) => (
                                    <SelectItem key={w.id} value={w.value}>{w.value}</SelectItem>
                                  ))
                                ) : (
                                  <SelectItem value="-">-</SelectItem>
                                )}
                          </SelectContent>
                        </Select>
                    ) : (
                      <div className="font-medium h-10 flex items-center">{cabinet.systemboard || '-'}</div>
                    )}
                  </div>
                  </CardContent>
              </Card>

              <Card className="bezel">
                <CardHeader>
                  <CardTitle>Technical Details</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  
                  {/* --- Master Type --- */}
                  <div className="space-y-2">
                    <Label>Voltage</Label>
                    {editing ? (
                      <Select 
                        value={formData.voltage || "Common"} 
                        onValueChange={(v) => setFormData({ ...formData, voltage: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          {voltages.map((type) => (
                            <SelectItem key={type} value={type}>{type}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="font-medium h-10 flex items-center">{cabinet.voltage || '-'}</div>
                    )}
                  </div>

                  {/* --- Coin Mechanism --- */}
                  <div className="space-y-2">
                    <Label>Coin Mechanism</Label>
                    {editing ? (
                      <Select 
                        value={formData.coin_mechanism || "euro"} 
                        onValueChange={(v) => setFormData({ ...formData, coin_mechanism: v })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select coin mech" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="euro">Euro</SelectItem>
                          <SelectItem value="yen">Yen</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="font-medium h-10 flex items-center">{cabinet.coin_mechanism || '-'}</div>
                    )}
                  </div>

                  {/* --- Audio System --- */}
                  <div className="space-y-2">
                    <Label>Audio System</Label>
                    {editing ? (
                      <Select 
                        value={formData.audio_system || "stereo"} 
                        onValueChange={(v) => setFormData({ ...formData, audio_system: v })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select audio" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mono">Mono</SelectItem>
                          <SelectItem value="stereo">Stereo</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="font-medium h-10 flex items-center">{cabinet.audio_system || '-'}</div>
                    )}
                  </div>

                  {/* --- Playing Position --- */}
                  <div className="space-y-2">
                    <Label>Playing Position</Label>
                    {editing ? (
                      <Select 
                        value={formData.playing_position || "seated"} 
                        onValueChange={(v) => setFormData({ ...formData, playing_position: v })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select position" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="standing">Standing</SelectItem>
                          <SelectItem value="seated">Seated</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="font-medium h-10 flex items-center">{cabinet.playing_position || '-'}</div>
                    )}
                  </div>

                  {/* --- Number of Players --- */}
                  <div className="space-y-2">
                    <Label>Number of Players</Label>
                    {editing ? (
                      <div className="flex items-center space-x-2">
                        <Button 
                          variant="outline" size="icon" className="h-10 w-10"
                          onClick={() => setFormData(prev => ({ ...prev, number_of_players: Math.max(1, (prev.number_of_players || 1) - 1) }))}
                        >
                          -
                        </Button>
                        <div className="h-10 flex-1 flex items-center justify-center border rounded-md font-medium">
                          {formData.number_of_players || 1}
                        </div>
                        <Button 
                          variant="outline" size="icon" className="h-10 w-10"
                          onClick={() => setFormData(prev => ({ ...prev, number_of_players: Math.min(16, (prev.number_of_players || 1) + 1) }))}
                        >
                          +
                        </Button>
                      </div>
                    ) : (
                      <div className="font-medium h-10 flex items-center">{cabinet.number_of_players || 1}</div>
                    )}
                  </div>

                  {/* --- Controls --- */}
                  <div className="space-y-2">
                    <Label>Controls</Label>
                    {editing ? (
                      <Select 
                        value={formData.controls?.[0] || ""} 
                        onValueChange={(v) => setFormData({ ...formData, controls: [v] })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select controls" /></SelectTrigger>
                        <SelectContent>
                          {(lists.control || []).map((item) => (
                            <SelectItem key={item.id} value={item.value}>{item.value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="font-medium h-10 flex items-center">{cabinet.controls?.[0] || '-'}</div>
                    )}
                  </div>

                  {/* --- Buttons --- */}
                  <div className="space-y-2">
                    <Label>Buttons</Label>
                    {editing ? (
                      <Select 
                        value={formData.buttons?.[0] || ""} 
                        onValueChange={(v) => setFormData({ ...formData, buttons: [v] })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select buttons" /></SelectTrigger>
                        <SelectContent>
                          {(lists.button || []).map((item) => (
                            <SelectItem key={item.id} value={item.value}>{item.value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="font-medium h-10 flex items-center">{cabinet.buttons?.[0] || '-'}</div>
                    )}
                  </div>

                  {/* --- Topper --- */}
                  <div className="space-y-2">
                    <Label>Topper</Label>
                    {editing ? (
                      <Select 
                        value={formData.topper || ""} 
                        onValueChange={(v) => setFormData({ ...formData, topper: v })}
                      >
                        <SelectTrigger><SelectValue placeholder="Select topper" /></SelectTrigger>
                        <SelectContent>
                          {(lists.topper || []).map((item) => (
                            <SelectItem key={item.id} value={item.value}>{item.value}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="font-medium h-10 flex items-center">{cabinet.topper || '-'}</div>
                    )}
                  </div>

                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="history">
              <Card className="bezel">
                <CardContent className="p-0">
                  <div className="divide-y">
                    {history.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground">No history available</div>
                    ) : (
                      history.map((item) => (
                        <div key={item.id} className="p-4 flex justify-between items-center hover:bg-muted/50">
                          <div>
                            <p className="text-sm font-medium">
                              {item.field_name === 'created' ? 'Cabinet created' : 
                               item.field_name === 'deleted' ? 'Cabinet deleted' :
                               `Changed ${item.field_name}`}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              by {item.changed_by_login}
                            </p>
                          </div>
                          <div className="text-right">
                             <div className="text-xs bg-secondary px-2 py-1 rounded">
                               {item.old_value ? `${item.old_value} → ` : ''} {item.new_value}
                             </div>
                             <p className="text-xs text-muted-foreground mt-1">{formatDate(item.timestamp)}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="operations">
              <Card className="mb-4">
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                        <CardTitle className="text-lg">Financial Summary</CardTitle>
                    </CardHeader>
                                        <CardContent>
                        <div className="grid grid-cols-2 gap-8 mb-6">
                            {/* Dépenses */}
                            <div className="space-y-1">
                                <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Investment</span>
                                <div className="text-2xl font-bold text-red-600">
                                    {formatCurrency(totalInvested)}
                                </div>
                                <p className="text-xs text-muted-foreground">Purchase + Repairs + Transport</p>
                            </div>

                            {/* Recettes / Vente */}
                            <div className="space-y-1">
                                <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Sale Price</span>
                                {isSold ? (
                                    <>
                                        <div className="text-2xl font-bold text-green-600">
                                            {formatCurrency(salePrice)}
                                        </div>
                                        <div className={`text-sm font-medium ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                            Net Profit: {profit > 0 ? '+' : ''}{formatCurrency(profit)}
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-2xl font-bold text-muted-foreground/30">
                                        -
                                    </div>
                                )}
                            </div>
                        </div>

                        {Object.keys(investorBreakdown).length > 0 && (
                            <div className="pt-4 border-t">
                                <span className="text-xs font-semibold text-muted-foreground uppercase mb-3 block">Investors Breakdown</span>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                    {Object.entries(investorBreakdown).map(([name, amount]) => {
                                        const contribs = investorContributions[name] || [];
                                        const box = (
                                            <div className="flex flex-col bg-muted/40 p-2 rounded border cursor-help h-full">
                                                <span className="text-xs font-medium truncate" title={name}>{name}</span>
                                                <span className="text-sm font-bold tabular-nums">{formatCurrency(amount)}</span>
                                            </div>
                                        );
                                        if (contribs.length < 2) return <div key={name}>{box}</div>;
                                        return (
                                            <TooltipProvider key={name} delayDuration={100}>
                                                <Tooltip>
                                                    <TooltipTrigger asChild><div>{box}</div></TooltipTrigger>
                                                    <TooltipContent className="max-w-[260px]">
                                                        <div className="space-y-1">
                                                            {contribs.map((c, i) => (
                                                                <div key={i} className="flex justify-between gap-4 text-xs">
                                                                    <span className="font-medium capitalize">{t(`operations.${c.type}`)}</span>
                                                                    <span className="font-mono tabular-nums">{formatCurrency(c.amount)}</span>
                                                                </div>
                                                            ))}
                                                            <div className="flex justify-between gap-4 text-xs pt-1 mt-1 border-t border-border/60 font-bold">
                                                                <span>Total</span>
                                                                <span className="font-mono tabular-nums">{formatCurrency(amount)}</span>
                                                            </div>
                                                        </div>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </CardContent>

                </Card>
              <Card className="bezel">
                 <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-base">{t('operations.title')}</CardTitle>

                    {canEdit && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => navigate(`/operations?cabinet_id=${id}&action=create`)}
                      >
                        <Plus className="h-4 w-4 mr-2"/> Add
                      </Button>
                    )}

                    {canEdit && (
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() =>  navigate(`/operations?cabinet_id=${id}`)}
                      >
                        <ListFilter className="h-4 w-4 mr-2"/> Manage
                      </Button>
                    )}

                 </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y">
                    {operations.length === 0 ? (
                      <div className="p-4 text-center text-muted-foreground">No operations recorded</div>
                    ) : (
                      operations.map((op) => (
                        <div key={op.id} className="p-4 flex justify-between items-start gap-4">
                           <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div className={`p-2 rounded-full shrink-0 ${op.operation_type === 'sale' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-secondary/15 text-secondary'}`}>
                                 {op.operation_type === 'sale' ? <Receipt className="h-4 w-4"/> : <Receipt className="h-4 w-4"/>}
                              </div>
                              <div className="min-w-0">
                                 <p className="font-medium capitalize">{t(`operations.${op.operation_type}`)}</p>
                                 <p className="text-sm text-muted-foreground break-words">{op.description}</p>
                              </div>
                           </div>
                           <div className="flex flex-col items-end gap-0.5 text-right shrink-0">
                              {(op.operation_type === 'sale' || op.operation_type === 'purchase' || op.operation_type === 'purchase_system') && (
                                <p className={`font-bold ${op.operation_type === 'sale' ? 'text-green-600' : 'text-red-600'}`}>
                                  {op.operation_type === 'sale' ? '+' : '-'}{formatCurrency(op.price + (op.transport_cost || 0))}
                                </p>
                              )}

                              {op.estimated_cost && (
                                <p className={`font-bold text-yellow-600 text-sm`}>
                                  (Est.) -{formatCurrency(op.estimated_cost)}
                                </p>
                              )}

                              {(op.operation_type === 'purchase' || op.operation_type === 'purchase_system' || op.operation_type === 'repair') && (() => {
                                  const pb = (op.price_breakdown && op.price_breakdown.length > 0) ? op.price_breakdown : null;
                                  const count = pb ? pb.length : 1;
                                  const badge = (
                                    <Badge variant="primary" className={`text-[12px] h-5 px-1.5 font-normal text-muted-foreground border-border ${pb ? 'cursor-help' : 'cursor-default'}`}>
                                      {`${count} ${t('operations.investor')}${count > 1 ? 's' : ''}`}
                                    </Badge>
                                  );
                                  if (!pb) return badge;
                                  return (
                                    <TooltipProvider delayDuration={100}>
                                      <Tooltip>
                                        <TooltipTrigger asChild><span className="inline-flex">{badge}</span></TooltipTrigger>
                                        <TooltipContent className="max-w-[260px]">
                                          <div className="space-y-1">
                                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground pb-1 mb-1 border-b border-border/60">
                                              {t(`operations.${op.operation_type}`)} · {formatCurrency(pb.reduce((s, inv) => s + (parseFloat(inv.amount) || 0), 0))}
                                            </div>
                                            {pb.map((inv, i) => (
                                              <div key={i} className="flex justify-between gap-4 text-xs">
                                                <span className="font-medium">{inv.nickname || 'Unknown'}</span>
                                                <span className="font-mono tabular-nums">{formatCurrency(parseFloat(inv.amount) || 0)}</span>
                                              </div>
                                            ))}
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  );
                              })()}
                              <p className="text-xs text-muted-foreground">{new Date(op.date).toLocaleDateString()}</p>
                           </div>
                        </div>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="gallery" className="mt-4">
              <Card className="bezel">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Image className="h-5 w-5" /> Photo Gallery
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {cabinet.photos.length === 0 ? (
                    <div className="text-center py-10 text-muted-foreground">
                      No photos available
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                      {cabinet.photos.map((photo, index) => (
                        <div key={index} className="rounded-lg overflow-hidden border bg-muted aspect-video flex items-center justify-center relative group">
                          <img
                            src={`${API.replace('/api', '')}${photo}`}
                            alt={`Gallery ${index}`}
                            className="w-full h-full object-contain"
                          />
                          {canEdit && (
                            <div className="absolute top-2 right-2 flex gap-2">
                              <Button
                                variant="secondary"
                                size="icon"
                                className="h-8 w-8 opacity-80 hover:opacity-100 shadow-md"
                                title={t('cabinets.editPhoto')}
                                onClick={() => setEditingPhoto(photo)}
                              >
                                <Crop className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="destructive"
                                size="icon"
                                className="h-8 w-8 opacity-80 hover:opacity-100 shadow-md"
                                title={t('cabinets.deletePhoto')}
                                onClick={() => setPhotoToDelete(photo)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* --- ONGLET LIBRARY (SHEETS) --- */}
            <TabsContent value="library" className="mt-4">
              <Card className="bezel">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" /> Library (Manuals, Schematics, etc.)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    
                    {/* Liste des documents */}
                    {(cabinet.documents && cabinet.documents.length > 0) ? (
                      <div className="grid gap-2">
                        {cabinet.documents.map((doc, index) => {
                           // 1. Récupération du nom de fichier RÉEL (celui stocké sur le disque)
                           // doc est l'URL complète "/api/uploads/sheet_xxxx.pdf", on veut juste la fin
                           const fullFileName = doc.split('/').pop(); 

                           // 2. Calcul du nom d'AFFICHAGE (joli)
                           // On essaie d'enlever le préfixe technique "sheet_{id}_{uuid}_"
                           const parts = fullFileName.split('_');
                           // Si on a bien le format attendu (au moins 4 segments), on prend tout après le 3ème underscore
                           const displayName = parts.length >= 4 ? parts.slice(3).join('_') : fullFileName;

                           // 3. Choix de l'icône
                           const ext = fullFileName.split('.').pop().toLowerCase();
                           let Icon = FileText;
                           if (['xls', 'xlsx'].includes(ext)) Icon = FileSpreadsheet;
                           if (['pdf'].includes(ext)) Icon = FileType;

                           return (
                            <div key={index} className="flex items-center justify-between p-3 border rounded-md hover:bg-muted/50 transition-colors break-all">
                              <div className="flex items-center gap-3 min-w-0"> 
                                <Icon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                                
                                <a 
                                  href={`${API.replace('/api', '')}${doc}`}
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-sm font-medium hover:underline break-all"
                                  title={displayName}
                                >
                                  {displayName}
                                </a>
                              </div>
                              
                              {editing && (
                                <Button 
                                  variant="ghost" 
                                  size="icon" 
                                  className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                  // ICI : On passe bien fullFileName (le nom technique) pour la suppression
                                  onClick={() => handleDocDelete(fullFileName)}
                                >
                                  <Trash className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                           );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        No documents available.
                      </div>
                    )}


                    {/* Zone d'upload (visible uniquement en édition) */}
                    {editing && (
                      <div className="mt-4 pt-4 border-t">
                        <div className="flex items-center gap-4">
                           <Button variant="outline" onClick={() => docInputRef.current.click()} disabled={uploadingDoc}>
                              <Upload className="h-4 w-4 mr-2" />
                              {uploadingDoc ? "Uploading..." : "Upload Document"}
                           </Button>
                           <span className="text-xs text-muted-foreground">PDF, Word, Excel, TXT</span>
                        </div>
                        <input 
                          type="file" 
                          ref={docInputRef} 
                          className="hidden" 
                          accept=".pdf,.doc,.docx,.xls,.xlsx,.txt" 
                          onChange={handleDocUpload} 
                        />
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>


            {/* --- ONGLET LINKS --- */}
            <TabsContent value="links" className="mt-4">
              <Card className="bezel">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <LinkIcon className="h-5 w-5" /> External Links
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    
                    {/* Mode Édition : Ajouter un lien */}
                    {editing && (
                      <div className="flex gap-2 mb-4">
                        <Input 
                          placeholder="https://example.com" 
                          value={newLink}
                          onChange={(e) => setNewLink(e.target.value)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddLink()}
                        />
                        <Button onClick={handleAddLink} type="button">
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    )}

                    {/* Liste des liens */}
                    {((editing ? formData.links : cabinet.links) || []).length > 0 ? (
                      <div className="grid gap-2">
                        {((editing ? formData.links : cabinet.links) || []).map((link, index) => (
                          <div key={index} className="flex items-center justify-between p-3 border rounded-md hover:bg-muted/50 transition-colors break-all">
                            <div className="flex items-center gap-3 overflow-hidden">
                              <ExternalLink className="h-4 w-4 text-primary flex-shrink-0" />
                              <a 
                                href={link} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-sm font-medium text-primary hover:underline break-all"
                              >
                                {link}
                              </a>
                            </div>

                            {editing && (
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                                onClick={() => handleRemoveLink(index)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-muted-foreground text-sm">
                        No links added yet.
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </div>
      </div>

      <PhotoEditor
        open={!!editingPhoto}
        imageUrl={editingPhoto ? `${API.replace('/api', '')}${editingPhoto}` : null}
        saving={savingEdit}
        onClose={() => setEditingPhoto(null)}
        onSave={handleSavePhotoEdit}
      />

      <AlertDialog open={!!photoToDelete} onOpenChange={() => setPhotoToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete photo?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the photo from the server.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePhotoDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  </div>
  );
};

// Icône manquante dans les imports, j'ajoute un fallback simple
const Plus = ({ className }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M5 12h14M12 5v14"/>
  </svg>
);

export default CabinetDetail;
